import type { ReactNode } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'

import styles from './PageTransition.module.css'

/** The five tab roots: switching between them is a lateral move, not a push. */
const TAB_ROOTS: ReadonlySet<string> = new Set(['/', '/markets', '/signals', '/trade', '/wallet'])

/** The first screen of the session arrives without a direction. */
let firstRender = true

/**
 * A screen arrives from the direction it came from, the way a native
 * navigation stack behaves: a push slides in from the right, a pop from the
 * left, a tab switch crossfades. Only the arriving screen animates — there is
 * no exit choreography to wait through, so nothing here can be "stuck" mid
 * transition, and the whole thing collapses to zero under reduced motion
 * through the duration token.
 *
 * Keyed on the pathname, not the full location: a market change inside the
 * terminal (`?market=`) is the same screen and must not re-enter.
 */
export function PageTransition({
  children,
  tabbed = false,
}: {
  children: ReactNode
  /** Under the floating tab bar: every scroll area and footer keeps its end free of it. */
  tabbed?: boolean
}): JSX.Element {
  const location = useLocation()
  const navigationType = useNavigationType()

  const isRoot = TAB_ROOTS.has(location.pathname)
  let variant = styles.fade
  if (firstRender) {
    firstRender = false
  } else if (navigationType === 'POP' && !isRoot) {
    variant = styles.fromLeft
  } else if (!isRoot) {
    variant = styles.fromRight
  }

  return (
    <div key={location.pathname} className={[styles.page, variant, tabbed ? styles.tabbed : ''].filter(Boolean).join(' ')}>
      {children}
    </div>
  )
}
